/**
 * EntityHeader — header for a detail page about a SPECIFIC entity
 * (project, campaign, account, …).
 *
 * Distinguishes from `<PageHeader>` (index/list pages):
 *   - PageHeader: title describes the CATEGORY ("Проекты").
 *   - EntityHeader: title describes the SPECIFIC instance ("12 / За клики /
 *     Перформ-кампании") with an identity-block (icon + name + category +
 *     subtitle).
 *
 * Anatomy:
 *
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ [icon]  Name              [action] [action] [action]         │
 *   │         Category                                              │
 *   │         Subtitle                                              │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * `icon` is a free slot (Logo, brand icon, avatar). The identity column
 * (icon + name + category + subtitle) sits on the left; actions on the
 * right. On narrow viewports the row reflows: actions slide under the
 * identity block.
 *
 * @format
 */

import {forwardRef, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text} from 'ui/theme/typography';
import {Row} from 'ui/components/layout/Row';

export interface EntityHeaderProps {
  /** Identity icon — Logo, brand icon, avatar, etc. */
  icon?: ReactNode;
  /** Entity name (h1). */
  name: ReactNode;
  /** Category label — secondary heading inline with name (e.g. "За клики"). */
  category?: ReactNode;
  /** Subtitle — small muted line under the identity (e.g. "Перформ-кампании"). */
  subtitle?: ReactNode;
  /** Right-side actions row. */
  actions?: ReactNode;
  className?: string;
}

const IconSlot = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  flexShrink: 0,
});

const IdentityColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: '1 1 auto',
});

const NameRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(3),
  minWidth: 0,
  flexWrap: 'wrap',
}));

const Name = styled('h1')(({theme}) => ({
  ...text('heading.l'),
  color: theme.palette.surface.onSurface,
  margin: 0,
  minWidth: 0,
}));

const Category = styled(Box)(({theme}) => ({
  ...text('label.m'),
  color: theme.palette.surface.onSurfaceLight,
}));

const Subtitle = styled('p')(({theme}) => ({
  ...text('body.s'),
  color: theme.palette.surface.onSurfaceMuted,
  margin: 0,
  marginTop: theme.spacing(0.5),
}));

const ActionsSlot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
});

export const EntityHeader = forwardRef<HTMLElement, EntityHeaderProps>(function EntityHeader(
  {icon, name, category, subtitle, actions, className},
  ref,
) {
  return (
    <Row
      ref={ref as React.Ref<HTMLDivElement>}
      as='header'
      gap='m'
      alignY='start'
      justify='between'
      reflowAt='md'
      className={className}
    >
      <Row gap='m' alignY='start'>
        {icon != null && <IconSlot>{icon}</IconSlot>}
        <IdentityColumn>
          <NameRow>
            <Name>{name}</Name>
            {category != null && <Category>{category}</Category>}
          </NameRow>
          {subtitle != null && <Subtitle>{subtitle}</Subtitle>}
        </IdentityColumn>
      </Row>
      {actions != null && <ActionsSlot>{actions}</ActionsSlot>}
    </Row>
  );
});
